import { ComponentProps, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import styled from 'styled-components'
import { DEFAULT_FOLDER_ID } from '../../constants'
import {
    useDeferredInitialLoad,
    useGTLocalStorage,
    useGlobalKeyboardShortcuts,
    useIsMobile,
    useWindowSize,
} from '../../hooks'
import { Border, Colors, Dimensions, Shadows, Spacing, Typography } from '../../styles'
import {
    BREAKPOINTS,
    CALENDAR_COLLAPSED_WIDTH,
    CALENDAR_SIDEBAR_WIDTH,
    DEFAULT_VIEW_WIDTH,
    TOOLTIP_MAX_WIDTH,
} from '../../styles/dimensions'
import { icons } from '../../styles/images'
import { Motion, fadeIn } from '../../styles/motion'
import { GT_MODAL_ROOT_CLASS } from '../atoms/BaseModal'
import GTButton from '../atoms/buttons/GTButton'
import ReadOnlyBanner from '../billing/ReadOnlyBanner'
import TrialUpgradeBanner from '../billing/TrialUpgradeBanner'
import { useCalendarContext } from '../calendar/CalendarContext'
import CommandPalette from '../molecules/CommandPalette'
import ShellLogo from '../navigation_sidebar/ShellLogo'
import CalendarView, { TCalendarType } from '../views/CalendarView'
import NavigationView from '../views/NavigationView'

const COLLAPSE_BREAKPOINT = 1500
const MOBILE_APP_BAR_HEIGHT = '56px'
const GT_MODAL_ROOT_SELECTOR = `[data-gt-modal-root], .${GT_MODAL_ROOT_CLASS}`

type DialogContentProps = ComponentProps<typeof Dialog.Content>

interface MobileRouteState {
    isDetailRoute: boolean
    backPath: string
    title: string
}

const getMobileRouteState = (pathname: string): MobileRouteState => {
    const pathParts = pathname.split('/').filter(Boolean)
    const section = pathParts[0]
    const detailId = pathParts[1]
    const nestedDetailId = pathParts[2]

    switch (section) {
        case 'overview':
            return {
                isDetailRoute: pathParts.length >= 3,
                backPath: '/overview',
                title: 'Daily Overview',
            }
        case 'tasks':
            return {
                isDetailRoute: pathParts.length >= 3,
                backPath: detailId ? `/tasks/${detailId}` : '/tasks',
                title: 'Tasks',
            }
        case 'notes':
            return {
                isDetailRoute: pathParts.length >= 2,
                backPath: '/notes',
                title: 'Notes',
            }
        case 'goals':
            return {
                isDetailRoute: pathParts.length >= 2,
                backPath: '/goals',
                title: 'Goals',
            }
        case 'recurring-tasks':
            return {
                isDetailRoute: pathParts.length >= 2,
                backPath: '/recurring-tasks',
                title: 'Recurring Tasks',
            }
        case 'routines':
            return {
                isDetailRoute: pathParts.length >= 2,
                backPath: '/routines',
                title: 'Routines',
            }
        default:
            return {
                isDetailRoute: Boolean(nestedDetailId),
                backPath: '/overview',
                title: 'General Task',
            }
    }
}

// Week view with the list open is nav + list + week. On ultrawide screens the list keeps its details
// pane too (see useShowDetailsPane), so the list column grows to hold both and the week keeps the
// larger share of the width.
const DefaultTemplateContainer = styled.div<{
    $calendarType: TCalendarType
    $showSidebar: boolean
    $showSidebarDetails: boolean
}>`
    display: grid;
    grid-template-columns: ${(props) =>
        props.$calendarType === 'day'
            ? `min-content minmax(300px, auto) max-content`
            : props.$showSidebar && props.$showSidebarDetails
            ? `min-content minmax(calc(${DEFAULT_VIEW_WIDTH} + ${CALENDAR_SIDEBAR_WIDTH}), 2fr) minmax(0, 3fr)`
            : props.$showSidebar
            ? `min-content min-content auto`
            : `min-content auto`};
    grid-auto-flow: column;
    grid-template-rows: 100%;
    height: 100vh;
    background-color: ${Colors.background.page};
    position: relative;
    a {
        color: ${Colors.text.ink};
    }
    .tooltip {
        box-shadow: ${Shadows.modal} !important;
        border-radius: ${Border.radius.medium} !important;
        font-family: ${Typography.fontFamily.sans} !important;
        ${Typography.body.medium};
        padding: ${Spacing._8} !important;
        max-width: ${TOOLTIP_MAX_WIDTH};
    }

    ${Dimensions.mediaQuery.phone} {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: ${MOBILE_APP_BAR_HEIGHT} minmax(0, 1fr);
        grid-auto-flow: row;
        width: 100%;
        min-width: 0;
        height: 100vh;
        height: 100dvh;
        overflow: hidden;
    }
`

const TasksandDetails = styled.div<{ $showMobileDetail: boolean }>`
    flex: 1;
    flex-direction: row;
    display: flex;
    position: relative;
    overflow: hidden;
    background-color: inherit;
    min-width: 0;

    ${Dimensions.mediaQuery.phone} {
        grid-row: 2;
        width: 100%;

        > * {
            flex: 1;
            min-width: 0;
            width: 100%;
        }

        ${({ $showMobileDetail }) =>
            $showMobileDetail
                ? `
                    > *:first-child:not(:only-child) {
                        display: none;
                    }
                `
                : `
                    > [data-mobile-pane='detail'] {
                        display: none;
                    }
                `}
    }
`

// Stays mounted while the calendar swaps between its strip and the day sidebar, so the width can
// animate. Week view has no fixed width (it fills the screen), so it switches without animating.
const CalendarSlot = styled.div<{ $width?: string }>`
    display: flex;
    justify-content: flex-end;
    height: 100%;
    min-width: 0;
    overflow: hidden;
    ${({ $width }) => $width && `width: ${$width};`}
    transition: width ${Motion.panel};
    > * {
        flex-shrink: 0;
        animation: ${fadeIn} ${Motion.enter};
    }
`
// The full-width week calendar has no MainColumn, so the read-only bar stacks above the calendar
// there. The wrapper is always mounted so toggling day/week never remounts the calendar.
const CalendarColumn = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    > :last-child {
        flex: 1;
        min-height: 0;
    }
`
const MainColumn = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    background-color: inherit;

    ${Dimensions.mediaQuery.phone} {
        grid-row: 2;
        width: 100%;
    }
`

// `data-mobile-app-bar` is the handle the mobile acceptance spec asserts the bar on; the bar is a
// bare div with no text of its own, so there is nothing else stable to select. Same idea as
// `data-mobile-pane` below.
const MobileAppBar = styled.div.attrs({ 'data-mobile-app-bar': '' })`
    display: none;

    ${Dimensions.mediaQuery.phone} {
        grid-row: 1;
        display: flex;
        align-items: center;
        gap: ${Spacing._12};
        min-width: 0;
        /* No top inset: the status bar stays its own band (see apple-mobile-web-app-status-bar-style
           in src/index.html), so only the landscape corners can reach this row. */
        padding: ${Spacing._8} ${Dimensions.safeGutter(Spacing._12, 'right')} ${Spacing._8}
            ${Dimensions.safeGutter(Spacing._12, 'left')};
        background-color: ${Colors.background.page};
        border-bottom: 1px solid ${Colors.background.border};
        box-sizing: border-box;
        z-index: 2;
    }
`

const MobileTitle = styled.div`
    ${Typography.title.medium};
    color: ${Colors.text.ink};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const MobileDrawerOverlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.28);
    z-index: 20;
    touch-action: none;
`

const MobileDrawerContent = styled(Dialog.Content)`
    position: fixed;
    inset: 0 auto 0 0;
    width: min(82vw, 320px);
    max-width: 100vw;
    background-color: ${Colors.background.panel};
    z-index: 21;
    box-shadow: ${Shadows.modal};
    outline: none;
    /* Pinned to the left edge and the full height of the screen, so it meets the hardware on three
       sides. Padding rather than insetting the panel itself: the background should still run under
       the notch, only the navigation rows need to stay clear of it. */
    padding-left: ${Dimensions.safeArea.left};
    padding-bottom: ${Dimensions.safeArea.bottom};
    box-sizing: border-box;
`

const HiddenDialogTitle = styled(Dialog.Title)`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`
const FloatingCaptureButton = styled(GTButton)`
    && {
        position: fixed;
        right: ${Dimensions.safeGutter(Spacing._16, 'right')};
        bottom: ${Dimensions.safeGutter(Spacing._24, 'bottom')};
        z-index: 3;
        display: flex;
        width: ${Spacing._56};
        height: ${Spacing._56};
        min-width: ${Spacing._56};
        min-height: ${Spacing._56};
        border-radius: 50%;
        color: ${Colors.control.primary.label};
        background-color: ${Colors.control.primary.bg};
        border: ${Border.stroke.medium} solid ${Colors.control.primary.stroke};
        box-shadow: ${Shadows.button.hover};

        &:active:enabled,
        &:focus:enabled {
            background-color: ${Colors.control.primary.highlight};
        }
    }
`
interface DefaultTemplateProps {
    children: React.ReactNode
}

const DefaultTemplate = ({ children }: DefaultTemplateProps) => {
    useGlobalKeyboardShortcuts()
    const { width } = useWindowSize()
    const isMobile = useIsMobile()
    const shouldLoadSecondaryQueries = useDeferredInitialLoad()
    const location = useLocation()
    const navigate = useNavigate()
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false)
    const {
        calendarType,
        dayViewDate,
        showTaskToCalSidebar,
        isCollapsed: isCalCollapsed,
        setIsCollapsed: setIsCalCollapsed,
        setCalendarType,
        setDate,
        setIsTaskListPinned,
    } = useCalendarContext()
    const [isNavCollapsed, setIsNavCollapsed] = useGTLocalStorage('navigationCollapsed', false)
    const mobileRouteState = useMemo(() => getMobileRouteState(location.pathname), [location.pathname])

    useLayoutEffect(() => {
        if (!width) return
        if (width < Dimensions.BREAKPOINTS.phone) {
            if (!isCalCollapsed) setIsCalCollapsed(true)
            return
        }
        if (width < COLLAPSE_BREAKPOINT) {
            if (!isNavCollapsed) setIsNavCollapsed(true)
            if (!isCalCollapsed) setIsCalCollapsed(true)
        } else if (width > COLLAPSE_BREAKPOINT) {
            if (isNavCollapsed) setIsNavCollapsed(false)
            if (isCalCollapsed) setIsCalCollapsed(false)
        }
    }, [width])

    // Crossing into ultrawide opens the week with the current list pinned beside it; crossing back out
    // undoes that, but only if this effect made the switch. The pin is session state in CalendarContext,
    // so the saved task-list preference is never overwritten. Acting on the crossing alone means a manual
    // day/week toggle or a closed list sticks until the window next changes tier. A passive effect, not
    // a layout effect, so it runs after CalendarView's mount effect resets the calendar to day view.
    const isUltrawide = width >= BREAKPOINTS.ultrawide
    const wasUltrawide = useRef(false)
    const autoWeek = useRef(false)
    useEffect(() => {
        if (!width || isUltrawide === wasUltrawide.current) return
        wasUltrawide.current = isUltrawide
        if (isUltrawide) {
            if (calendarType !== 'day') return
            autoWeek.current = true
            setCalendarType('week')
            setDate((date) => date.minus({ days: date.weekday % 7 }))
            setIsTaskListPinned(true)
        } else if (autoWeek.current) {
            if (calendarType === 'week') {
                setCalendarType('day')
                setDate(dayViewDate)
            }
            setIsTaskListPinned(false)
            autoWeek.current = false
        }
    }, [isUltrawide])

    useEffect(() => {
        setIsMobileDrawerOpen(false)
    }, [location.pathname])

    useEffect(() => {
        if (!isMobile || !isMobileDrawerOpen) return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [isMobile, isMobileDrawerOpen])

    const handleMobileDrawerPointerDownOutside: NonNullable<DialogContentProps['onPointerDownOutside']> = (event) => {
        const target = event.detail.originalEvent.target
        if (target instanceof HTMLElement && target.closest(GT_MODAL_ROOT_SELECTOR)) {
            event.preventDefault()
        }
    }
    const focusVisibleCreateInput = () => {
        const createInput = Array.from(
            document.querySelectorAll<HTMLInputElement>('[data-default-task-capture-input]')
        ).find((input) => input.getClientRects().length > 0)
        createInput?.focus()
        return Boolean(createInput)
    }
    const focusMobileTaskCapture = () => {
        if (focusVisibleCreateInput()) return

        flushSync(() => {
            navigate(`/tasks/${DEFAULT_FOLDER_ID}`, { replace: true, state: { focusCreateInputOnLaunch: true } })
        })
        focusVisibleCreateInput()
        if (window.requestAnimationFrame) {
            window.requestAnimationFrame(focusVisibleCreateInput)
        } else {
            window.setTimeout(focusVisibleCreateInput, 0)
        }
    }

    const showMainColumn = isMobile || calendarType === 'day' || showTaskToCalSidebar
    return (
        <DefaultTemplateContainer
            $calendarType={calendarType}
            $showSidebar={showTaskToCalSidebar}
            $showSidebarDetails={width >= BREAKPOINTS.ultrawideFull}
        >
            {/* Hidden by CSS on desktop, but mounting it there still ran its tooltips' Radix trees on
                every render. Gate on the same breakpoint so desktop never mounts them. */}
            {isMobile && (
                <MobileAppBar>
                    {mobileRouteState.isDetailRoute ? (
                        <GTButton
                            styleType="icon"
                            icon={icons.arrow_left}
                            onClick={() => navigate(mobileRouteState.backPath, { replace: true })}
                            tooltipText="Back"
                        />
                    ) : (
                        <GTButton
                            styleType="icon"
                            icon={icons.hamburger}
                            onClick={() => setIsMobileDrawerOpen(true)}
                            tooltipText="Menu"
                        />
                    )}
                    <ShellLogo />
                    <MobileTitle>{mobileRouteState.title}</MobileTitle>
                    <CommandPalette />
                </MobileAppBar>
            )}
            {!isMobile && <NavigationView isCollapsed={isNavCollapsed} setIsCollapsed={setIsNavCollapsed} />}
            {showMainColumn && (
                <MainColumn>
                    <ReadOnlyBanner />
                    <TrialUpgradeBanner shouldLoadSecondaryQueries={shouldLoadSecondaryQueries} />
                    <TasksandDetails $showMobileDetail={mobileRouteState.isDetailRoute}>{children}</TasksandDetails>
                </MainColumn>
            )}
            {!isMobile && (
                <CalendarColumn>
                    {!showMainColumn && <ReadOnlyBanner />}
                    <CalendarSlot
                        $width={
                            isCalCollapsed
                                ? CALENDAR_COLLAPSED_WIDTH
                                : calendarType === 'day'
                                ? CALENDAR_SIDEBAR_WIDTH
                                : undefined
                        }
                    >
                        <CalendarView initialType="day" shouldLoadSecondaryQueries={shouldLoadSecondaryQueries} />
                    </CalendarSlot>
                </CalendarColumn>
            )}
            <Dialog.Root modal={false} open={isMobile && isMobileDrawerOpen} onOpenChange={setIsMobileDrawerOpen}>
                <Dialog.Portal>
                    <MobileDrawerOverlay />
                    <MobileDrawerContent onPointerDownOutside={handleMobileDrawerPointerDownOutside}>
                        <HiddenDialogTitle>Navigation</HiddenDialogTitle>
                        <NavigationView
                            isCollapsed={false}
                            setIsCollapsed={() => undefined}
                            hideCollapseControl
                            hideCommandPalette
                        />
                    </MobileDrawerContent>
                </Dialog.Portal>
            </Dialog.Root>
            {isMobile && (
                <FloatingCaptureButton
                    data-mobile-add-task-button
                    styleType="icon"
                    icon={icons.plus}
                    tooltipText="Create task"
                    onClick={focusMobileTaskCapture}
                />
            )}
        </DefaultTemplateContainer>
    )
}

export default DefaultTemplate
